import {Directive, Injector, Optional} from '@angular/core';

import {addClass, setAttr, toBoolean, runtimeEventManager} from '@wm/core';

import { APPLY_STYLES_TYPE, styler } from '../../framework/styler';
import { IWidgetConfig } from '../../framework/types';
import { provideAsWidgetRef } from '../../../utils/widget-utils';
import {Parent} from "../../framework/parent.provider";
import {StylableComponent} from '../base/stylable.component';
import {register} from "../../framework/widget-props";

const DEFAULT_CLS = 'app-container';
const WIDGET_CONFIG: IWidgetConfig = {
    widgetType: 'wm-container',
    hostClass: DEFAULT_CLS
};

// Alignment matrix (assumes direction: 'row' as base)
const alignmentMatrix = {
  'top-left':       { justifyContent: 'flex-start', alignItems: 'flex-start' },
  'top-center':     { justifyContent: 'center',     alignItems: 'flex-start' },
  'top-right':      { justifyContent: 'flex-end',   alignItems: 'flex-start' },

  'middle-left':    { justifyContent: 'flex-start', alignItems: 'center' },
  'middle-center':  { justifyContent: 'center',     alignItems: 'center' },
  'middle-right':   { justifyContent: 'flex-end',   alignItems: 'center' },

  'bottom-left':    { justifyContent: 'flex-start', alignItems: 'flex-end' },
  'bottom-center':  { justifyContent: 'center',     alignItems: 'flex-end' },
  'bottom-right':   { justifyContent: 'flex-end',   alignItems: 'flex-end' },

  'start':          { justifyContent: 'space-between', alignItems: 'flex-start' },
  'center':         { justifyContent: 'space-between',  alignItems: 'center' },
  'end':            { justifyContent: 'space-between',  alignItems: 'flex-end' }

};

const alignmentIcons = ['top-left', 'top-center', 'top-right',
                        'middle-left', 'middle-center', 'middle-right',
                        'bottom-left', 'bottom-center', 'bottom-right']

const autoAlignmentsIcons = ['start', 'center', 'end']

@Directive({
    selector: '[wmContainer]',
    standalone: true,
    providers: [
        provideAsWidgetRef(ContainerComponent)
    ]
})
export class ContainerComponent extends StylableComponent {
    static initializeProps = register('wm-container');
    public direction: 'row' | 'column';
    public hasWrap: boolean = false;
    public alignment: string;
    public gap: string;
    public columngap: string;
    public spacing: string;
    public wrap :boolean;
    public clipContent: boolean;
    public showClipContent: boolean = false;
    public position: string;
    public overflowtype: string;
    public zIndex: string;
    public isNativeMobileApplicationType: boolean;

    constructor(inj: Injector, @Optional() public _viewParent: Parent) {
        super(inj, WIDGET_CONFIG, _viewParent);

        addClass(this.nativeElement, DEFAULT_CLS);
        styler(this.nativeElement, this, APPLY_STYLES_TYPE.CONTAINER);
        this.isNativeMobileApplicationType = runtimeEventManager.getService('projectService').isNativeMobileApplicationType();  
    }

    protected override onPropertyChange(key: string, nv: any, ov?: any) {
        switch (key) {
            case 'direction':
                this.direction = nv;
                if (nv === 'column') this.hasWrap = false; // disable wrap for column direction
                this.applyLayout(); // Re-apply entire layout
                setAttr(this.nativeElement, key, nv);
                break;
            case 'wrap':
                this.hasWrap = toBoolean(nv);
                this.wrap = nv;
                if(nv){
                    this.columngap = this.columngap || this.widgetProps['columngap'].value
                    // When wrap is true, gap is used for row spacing, columngap for column spacing
                    this.gap = this.gap || this.widgetProps['gap'].value

                }else{
                    this.gap = this.columngap || this.gap || this.widgetProps['gap']?.value || '';
                    this.columngap = ''
                }
                this.applyLayout(); // Re-apply entire layout
                this.updateAlignmentIcons();
                setAttr(this.nativeElement, key, nv);
                break;
            case 'alignment':
                if (nv) {
                    this.alignment = nv;
                    this.applyLayout(); // Re-apply entire layout
                    setAttr(this.nativeElement, key, nv);
                }
                break;
            case 'gap':
                this.gap = nv;
                this.handleSpacingChange(); 
                setAttr(this.nativeElement, key, nv);
                break;
            case 'columngap':
                this.columngap = nv;
                this.handleSpacingChange(); 
                setAttr(this.nativeElement, key, nv);
                break;
            case 'position':
                this.position = nv;
                this.handlePosition();
                setAttr(this.nativeElement, key, nv);
                break;
            case 'clipcontent':
                this.clipContent = nv;
                this.handleClipBehaviour();
                setAttr(this.nativeElement, key, nv);
                break;
            case 'active':
                this.widgetProps["content"].show=true
        }
        super.onPropertyChange(key, nv, ov);
    }

    // Determine whether the container should clip its content and configure scrolling behaviour accordingly.
    private handleClipBehaviour() {
        const width = this?.width ?? this?.$attrs?.get('width'); // Use recently updated width or freshly loaded from the markup
        const height = this?.height ?? this?.$attrs?.get('height'); //// Use recently updated height or freshly loaded from the markup

        const showClipContent = !(width === 'hug' && height === "hug");
        const clipContent = this.clipContent ?? this?.$attrs?.get('clipcontent'); // Use recently updated clipcontent or freshly loaded from the markup
        this.widgetProps['clipcontent'].show = showClipContent;

        if(showClipContent && clipContent) {
            this.widgetProps['overflow'].show = true;
        } else {
            this.widgetProps['overflow'].show = false;
            this.widgetProps['overflow'].value = 'No scrolling';
            this.overflowtype = 'No scrolling';
            setAttr(this.nativeElement, 'overflow', 'No scrolling');
        }

        const overflow = this.overflowtype?.toLowerCase();
        switch (overflow) {
            case 'vertical':
                this.$element.css({
                    'overflow-x': 'hidden',
                    'overflow-y': 'scroll'
                });
                break;

            case 'horizontal':
                this.$element.css({
                    'overflow-x': 'scroll',
                    'overflow-y': 'hidden'
                });
                break;

            case 'both directions':
                this.$element.css({
                    'overflow': 'scroll'
                });
                break;

            case 'no scrolling':
                if (clipContent) {
                    this.$element.css({ 'overflow': 'hidden' });
                } else {
                    this.$element.css({ 'overflow': '' });
                }
                break;

            default:
                break;
        }
    }

    // Toggle and apply positional behaviour and expose the z-index control when position is not relative.
    private handlePosition() {
        if (this.position === 'sticky') {
            this.widgetProps['zindex'].show = true;
            // Apply sticky styles
            this.$element.css({
                'position': 'sticky',
                'top': '0',    // Sticks to top when scrolling vertically
                'left': '0',   // Sticks to left when scrolling horizontally
                'z-index': this.zIndex
            });
        } else {
            this.widgetProps['zindex'].show = false;
            // Reset to default (static/relative behavior)
            this.$element.css({
                'position': '',
                'top': '',
                'left': '',
                'z-index': ''
            });
        }
    }

    handleWrap() {
        this.hasWrap = !this.hasWrap;
        if (this.widgetProps['wrap'])
            this.widgetProps['wrap'].value = this.hasWrap;
    }

    protected override onStyleChange(key: string, nv: any, ov?: any) {
        if (key === 'alignment' && nv) {
            this.alignment = nv;
            this.applyLayout();
        }

        if (key === 'height') {
            if(this.isNativeMobileApplicationType){
                if (nv && nv.trim() !== '' && this.nativeElement.childElementCount > 0) {
                    this.$element.css('min-height', 'auto');
                } else {
                    this.$element.css('min-height', '28px');
                }
            } 
            this.$element.css({ 'height': '', 'align-self': '', 'flex-grow': ''  });
        }
        if(key === 'width' || key === 'height'){
            if (nv === 'fill') {
                if (key === 'width') this.$element.css('width', '100%');
                if (key === 'height') {
                    const parentElement = this.nativeElement.parentElement;
                    if(parentElement?.getAttribute('widgettype') === 'wm-container'){
                        this.$element.css({
                            'align-self': 'stretch',
                            'flex-grow': '1'
                        });       
                    }else{
                        this.$element.css('height', '100%')
                    }
                };
            } else if (nv === 'hug') {
                if (key === 'width') this.$element.css('width', 'fit-content');
                if (key === 'height') this.$element.css('height', 'fit-content');
            } else {
                this.$element.css('flex-shrink', '0'); // Don't shrink when size is fixed
            }
        } else if (key === 'padding') {
            this.$element.css('padding', nv)
        }
        if(key === 'width' || key === 'height' || key === 'padding'){
            setAttr(this.nativeElement, key, nv);
        }

        if(key === 'width' || key === 'height') {
            this.handleClipBehaviour();
        }

        else if(key === 'overflow') {
            this.overflowtype = nv;
            this.handleClipBehaviour();
            setAttr(this.nativeElement, key, nv);
        }

        else if(key === 'zindex') {
            this.zIndex = nv;
            this.handlePosition();
            setAttr(this.nativeElement, key, nv);
        }
        super.onStyleChange(key, nv, ov);
    }


    //  handles both alignment and spacing
    private applyLayout() {
        const base = (alignmentMatrix as any)[this.alignment];
        if (!base) return;

        const isRow = this.direction === 'row';
        const el = this.$element;

        // Reset all flex properties
        el.css({
            display: 'flex',
            'flex-direction': this.direction,
            'flex-wrap': this.hasWrap ? 'wrap' : 'nowrap',
            gap: '',
            'row-gap': '',
            'column-gap': '',
            'justify-content': '',
            'align-content': '',
            'align-items': ''
        });

        // Get base alignment values
        let baseJustifyContent = isRow ? base.justifyContent : base.alignItems;
        let baseAlignItems = isRow ? base.alignItems : base.justifyContent;

        // Apply layout based on wrap and spacing configuration
        if (!this.hasWrap) {
            this.applyNoWrapLayout(el, baseJustifyContent, baseAlignItems);
        } else {
            this.applyWrapLayout(el, baseJustifyContent, baseAlignItems);
        }
        this.spacing = this.isAutoAlignment() ? 'auto' : ''
    }

     /**
     * Apply layout for non-wrapping containers
     */
    private applyNoWrapLayout(el: any, baseJustifyContent: string, baseAlignItems: string) {
        if (this.gap === 'auto') {
            // Auto spacing mode
        const base = (alignmentMatrix as any)[this.alignment];
            el.css({
                'justify-content': base.justifyContent,
                'align-items': base.alignItems,
            });
        } else if (this.gap && this.gap !== 'auto') {
            // Fixed gap mode
            el.css({
                'justify-content': baseJustifyContent,
                'align-items': baseAlignItems, 
                'gap': `${this.gap}px`
            });
        } else {
            // No gap specified
            el.css({
                'justify-content': baseJustifyContent,
                'align-items': baseAlignItems,
            });
        }
    }

    /**
     * Apply layout for wrapping containers
     */
    private applyWrapLayout(el: any, baseJustifyContent: string, baseAlignItems: string) {
        const columngap = this.columngap;
        const rowgap = this.gap;

        // Both gaps are auto
        if (columngap === 'auto' && rowgap === 'auto') {
            el.css({
                'justify-content': baseJustifyContent,
                'align-items': baseAlignItems,
                'align-content': baseJustifyContent
            });
        }
        // Column gap auto, row gap fixed
        else if (columngap === 'auto' && rowgap && rowgap !== 'auto') {
            el.css({
                'justify-content': baseJustifyContent,
                'align-items': baseAlignItems,
                'align-content': baseAlignItems,
                'row-gap': `${rowgap}px`
            });
        }
        // Row gap auto, column gap fixed
        else if (rowgap === 'auto' && columngap && columngap !== 'auto') {
            el.css({
                'justify-content': baseJustifyContent,
                'align-items': baseAlignItems,
                'align-content': 'space-between',
                'column-gap': `${columngap}px`
            });
        }
        // Both gaps have fixed values
        else if (columngap && columngap !== 'auto' && rowgap && rowgap !== 'auto') {
            if (columngap === rowgap) {
                el.css({
                    'justify-content': baseJustifyContent,
                    'align-items': baseAlignItems,
                    'align-content': baseAlignItems,
                    'gap': `${columngap}px`
                });
            } else {
                el.css({
                    'justify-content': baseJustifyContent,
                    'align-items': baseAlignItems, 
                    'align-content': baseAlignItems,
                    'gap': `${rowgap}px ${columngap}px`
                });
            }
        }
        // Use gap if available, otherwise base alignment
        else if (this.gap && this.gap !== 'auto') {
            el.css({
                'justify-content': baseJustifyContent,
                'align-items': baseAlignItems,
                'align-content': baseAlignItems,
                'gap': `${this.gap}px`
            });
        }
        // Default case - use base alignment
        else {
            el.css({
                'justify-content': baseJustifyContent,
                'align-items': baseAlignItems,
                'align-content': baseAlignItems
            });
        }
    }

    private handleSpacingChange() {
        // Update alignment icons based on spacing mode
        this.updateAlignmentIcons();
        
        // Apply the complete layout with new spacing
        this.applyLayout();
        this.spacing = this.isAutoAlignment() ? 'auto' : ''
    }

    /**
     * Update alignment icons based on current spacing mode
     */
    private updateAlignmentIcons() {
        if (this.isAutoAlignment()) {
            this.widgetProps['alignment'].icons = autoAlignmentsIcons;
        } else {
            this.widgetProps['alignment'].icons = alignmentIcons;
        }
    }

    isAutoAlignment(){
        return !this.hasWrap ? this.gap === 'auto' : this.columngap === 'auto';
    }

}
